import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom, forkJoin, map, of, switchMap} from 'rxjs';
import {PlatformFolder} from '../../../models/platform-folder';
import {environment} from '../../../../environments/environment';

/** What the Audiense platform's `GET /api/folders` lists (the fields read here). */
interface PlatformFolderSummary {
  id: string;
  name: string;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A folder's resources, from `GET /api/folders/:id` (the fields read here). */
interface PlatformFolderDetail extends PlatformFolderSummary {
  resources: Array<{id: string; resolved: boolean; product?: string; title?: string}>;
}

const SOPRISM = 'soprism';

/** On: the dashboard's regular folders are the ones the Audiense platform keeps (US #1033). */
export const PLATFORM_FOLDERS_FLAG = 'di_platform_folders_enabled';

/**
 * A project just created reaches the platform's report list once its
 * `report.created` event is consumed, a few seconds later: until then, adding it
 * to a folder answers 422 `RESOURCE_NOT_AVAILABLE`.
 */
const LISTED_RETRY_EVERY_MS = 3_000;
const LISTED_RETRY_FOR_MS = 30_000;

/**
 * The user's folders as the Audiense platform keeps them (Folders), read with
 * the user's own token, which `ApiInterceptor` adds to every request.
 *
 * A `PlatformFolder` holds its Soprism projects by id; reports from other
 * products are only counted, since the dashboard cannot show them. Folders
 * shared with the user by someone else are left out. A failure rejects: the
 * caller decides what to show instead.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiPlatformFoldersService {
  private readonly apiUrl = environment.config.audiensePlatform.apiUrl;
  private readonly shellUrl = environment.config.audiensePlatform.shellUrl;

  constructor(private readonly http: HttpClient) {}

  getFolders(): Promise<PlatformFolder[]> {
    return firstValueFrom(
      this.http.get<{folders: PlatformFolderSummary[]}>(`${this.apiUrl}/api/folders`).pipe(
        map(({folders}) => folders.filter(folder => folder.isOwner)),
        switchMap(owned => owned.length === 0
          ? of([])
          : forkJoin(owned.map(folder => this.http.get<PlatformFolderDetail>(`${this.apiUrl}/api/folders/${folder.id}`)))),
        map(details => details.map(detail => this.toFolder(detail)))
      )
    );
  }

  async createFolder(name: string): Promise<PlatformFolder> {
    const detail = await firstValueFrom(this.http.post<PlatformFolderDetail>(`${this.apiUrl}/api/folders`, {name}));
    return this.toFolder(detail);
  }

  async renameFolder(folderId: string, name: string): Promise<void> {
    await firstValueFrom(this.http.patch(`${this.apiUrl}/api/folders/${folderId}`, {name}));
  }

  async deleteFolder(folderId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.apiUrl}/api/folders/${folderId}`));
  }

  /**
   * Adds a Soprism project to a folder. With `waitUntilListed`, for a project
   * just created, a "not available yet" answer is retried for up to 30 s.
   */
  async addProject(folderId: string, projectId: number, options: {waitUntilListed?: boolean} = {}): Promise<void> {
    const deadline = Date.now() + LISTED_RETRY_FOR_MS;
    for (;;) {
      try {
        await firstValueFrom(this.http.post(`${this.apiUrl}/api/folders/${folderId}/resources`, {
          resources: [{type: 'report', id: String(projectId)}],
        }));
        return;
      } catch (error) {
        const notListedYet = error instanceof HttpErrorResponse && error.status === 422
          && error.error?.error === 'RESOURCE_NOT_AVAILABLE';
        if (!options.waitUntilListed || !notListedYet || Date.now() + LISTED_RETRY_EVERY_MS > deadline) throw error;
        await new Promise(resolve => setTimeout(resolve, LISTED_RETRY_EVERY_MS));
      }
    }
  }

  async removeProject(folderId: string, projectId: number): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.apiUrl}/api/folders/${folderId}/resources/report/${projectId}`));
  }

  private toFolder(detail: PlatformFolderDetail): PlatformFolder {
    const opened = detail.resources.filter(resource => resource.resolved);
    const soprism = opened.filter(resource => resource.product === SOPRISM);

    return new PlatformFolder(
      detail.id,
      detail.name,
      soprism.map(resource => ({id: Number(resource.id), name: resource.title ?? ''})),
      opened.length - soprism.length,
      `${this.shellUrl}/folders/${detail.id}`,
      new Date(detail.createdAt),
      new Date(detail.updatedAt)
    );
  }
}
